import { useI18n } from '../../../i18n';
// ============================================
// TopProfitableProductsTable - Top products table
// ============================================

import { Pagination } from '../../../components/shared';
import { useClientPagination } from '../../../hooks/useClientPagination';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';

export interface TopProfitableProduct {
  code: string;
  name: string;
  quantity: number;
  revenue: number;
  /** null = BE chưa cung cấp dữ liệu giá vốn — hiển thị "—". */
  profit: number | null;
  costPrice: number | null;
}

interface Props {
  products: TopProfitableProduct[];
  /** Số dòng mỗi trang. */
  pageSize?: number;
  className?: string;
}

export function TopProfitableProductsTable({ products, pageSize = 10, className }: Props) {
  const { t } = useI18n();
  const money = useMoney();
  const page = useClientPagination(products, pageSize);

  // fmt chuyển vào trong component vì `useMoney()` là hook.
  const fmt = (v: number | null) =>
    v == null ? '—' : money.format(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className={cn('rounded-2xl bg-white p-6 shadow-sm', className)}>
      <h3 className="mb-4 text-base font-bold text-[#191B23]">{t('topProfitableProducts')}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#F1F5F9]">
              <th className="whitespace-nowrap pb-3 pr-2 text-left text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">{t('productCode')}</th>
              <th className="whitespace-nowrap pb-3 pr-2 text-left text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">{t('reportsProduct')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('quantity')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('revenue')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('profit')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('costPrice')}</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-sm text-slate-400">
                  {t('noSalesInPeriod')}
                </td>
              </tr>
            )}
            {page.pageItems.map((p, i) => (
              <tr key={`${p.code}-${i}`} className="border-b border-[#F8FAFC] last:border-0">
                <td className="py-3 pr-2 font-bold text-[#191B23]">{p.code}</td>
                <td className="py-3 pr-2 text-[#434655]">{p.name}</td>
                <td className="px-2 py-3 text-right text-[#434655]">{p.quantity}</td>
                <td className="px-2 py-3 text-right text-[#434655]">{fmt(p.revenue)}</td>
                <td className="px-2 py-3 text-right font-bold text-[#16A34A]">{fmt(p.profit)}</td>
                <td className="px-2 py-3 text-right text-[#434655]">{fmt(p.costPrice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {page.hasItems && (
        <Pagination
          variant="numbered"
          currentPage={page.currentPage}
          totalPages={page.totalPages}
          totalItems={page.totalItems}
          pageSize={page.pageSize}
          onPageChange={page.setPage}
          itemLabel={t('products')}
          className="mt-4 border-t border-[#F1F5F9] pt-4"
        />
      )}
    </div>
  );
}
